import React from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Minus, Plus, Phone, Info, PackagePlus, RotateCcw } from "lucide-react";
import { HARDWARE as HARDWARE_DEFAULT, SOFTWARE_PRICE_PER_STATION as SW_DEFAULT, SALES_TIERS as TIERS_DEFAULT } from "../lib/data";
import { useI18n } from "../i18n/I18nProvider";
import { getHardware } from "../i18n/content";
import { API } from "../lib/api";
import { useContent } from "../lib/ContentProvider";

// SKUs that form the "POS bundle" — only these are free when sales tier discount is 100%
const BUNDLE_SKUS = ["OJ-POS-15", "OJ-PAY-AMS1", "OJ-RCPT-80", "OJ-CASH"];
const BUNDLE_SET = new Set(BUNDLE_SKUS);

function fmt(amount, locale) {
  try {
    const loc = locale === "fr" ? "fr-CH" : locale === "de" ? "de-CH" : "en-CH";
    return new Intl.NumberFormat(loc, { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(Math.max(0, amount));
  } catch {
    return `CHF ${Math.max(0, amount).toFixed(0)}`;
  }
}

export default function Configurator() {
  const { t, locale } = useI18n();
  const { content } = useContent();
  const HARDWARE = content?.configurator?.hardware?.length ? content.configurator.hardware : HARDWARE_DEFAULT;
  const SALES_TIERS = content?.configurator?.sales_tiers?.length ? content.configurator.sales_tiers : TIERS_DEFAULT;
  const SOFTWARE_PRICE_PER_STATION = content?.configurator?.software_price_per_station ?? SW_DEFAULT;
  const [qty, setQty] = React.useState(() => Object.fromEntries(HARDWARE_DEFAULT.map((h) => [h.sku, h.default_qty])));
  const [withTerminal, setWithTerminal] = React.useState(true);
  const [tierId, setTierId] = React.useState("t0");
  const [contact, setContact] = React.useState({ full_name: "", business_name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = React.useState(false);

  const summaryRef = React.useRef(null);
  const [showMobileBar, setShowMobileBar] = React.useState(false);
  React.useEffect(() => {
    const el = summaryRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setShowMobileBar(!entry.isIntersecting),
      { threshold: 0.05 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const tier = SALES_TIERS.find((s) => s.id === tierId) || SALES_TIERS[0];
  const isFreeTier = tier.discount === 1;

  // Per-line net price.
  // At 100% (Free) tier: only the FIRST unit of each POS-bundle SKU is free; additional units charge full price.
  // Non-bundle items keep their full price at the 100% tier.
  // At other tiers: the discount applies uniformly to every item.
  const lineNetFor = (sku, gross, qtyForLine) => {
    if (isFreeTier) {
      if (!BUNDLE_SET.has(sku) || qtyForLine <= 0) return gross;
      const item = HARDWARE.find((h) => h.sku === sku);
      const price = item ? item.price : 0;
      const paidUnits = Math.max(0, qtyForLine - 1); // 1 unit free, rest paid
      return paidUnits * price;
    }
    return gross * (1 - tier.discount);
  };

  const localized = getHardware(locale);
  const items = HARDWARE.map((h) => {
    const loc = localized.find((x) => x.sku === h.sku);
    const name = locale === "fr" && loc ? loc.name : h.name;
    return { ...h, name, qty: qty[h.sku] || 0 };
  });
  const hardwareGross = items.reduce((acc, i) => acc + i.qty * i.price, 0);
  const hardwareNet = items.reduce((acc, i) => acc + lineNetFor(i.sku, i.qty * i.price, i.qty), 0);
  const hardwareDiscount = hardwareGross - hardwareNet;
  const stations = (qty["OJ-POS-15"] || 0) + (qty["OJ-KIOSK-238"] || 0);
  const baseLicense = stations * SOFTWARE_PRICE_PER_STATION;
  const licenseDiscount = withTerminal ? stations * 30 : 0;
  const softwareMonthly = Math.max(0, baseLicense - licenseDiscount);

  const inc = (sku, d) => setQty((s) => ({ ...s, [sku]: Math.max(0, Math.min(50, (s[sku] || 0) + d)) }));

  const addBundle = () => {
    setQty((s) => {
      const next = { ...s };
      BUNDLE_SKUS.forEach((sku) => {
        if ((next[sku] || 0) < 1) next[sku] = 1;
      });
      return next;
    });
  };

  const resetBundle = () => {
    setQty((s) => {
      const next = { ...s };
      BUNDLE_SKUS.forEach((sku) => {
        next[sku] = sku === "OJ-POS-15" ? 1 : 0;
      });
      return next;
    });
  };

  const submit = async (callRequest = false) => {
    if (!contact.full_name || !contact.email) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/configurator/quote`, {
        full_name: contact.full_name,
        business_name: contact.business_name,
        email: contact.email,
        phone: contact.phone,
        items: items.filter((i) => i.qty > 0).map((i) => ({ sku: i.sku, name: i.name, qty: i.qty, unit_price: i.price })),
        monthly_software: softwareMonthly,
        notes: `Sales tier: ${tier.label} · discount ${Math.round(tier.discount * 100)}% · withTerminal=${withTerminal}${callRequest ? " · callback requested" : ""}`,
        locale,
      });
      if (callRequest && contact.phone) {
        await axios.post(`${API}/callback-request`, {
          full_name: contact.full_name,
          phone: contact.phone,
          best_time: "asap",
          topic: `Configurator · ${fmt(hardwareNet, locale)} hardware (${tier.label})`,
          locale,
        });
        toast.success(t("callback.success"));
      } else {
        toast.success(t("configurator.saved"));
      }
    } catch (err) {
      toast.error("Could not save quote. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="configurator-page">
      <section className="relative pt-16 pb-12 sm:pt-24 sm:pb-16 border-b border-zinc-200 overflow-hidden">
        <div className="absolute inset-0 technical-grid pointer-events-none" />
        <div className="container-oj relative">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">Configurator</div>
          <h1 className="font-display mt-4 text-balance text-5xl sm:text-7xl font-black tracking-tighter leading-[0.95] text-oj-ink max-w-4xl">
            {t("configurator.title")}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-zinc-600">{t("configurator.sub")}</p>
        </div>
      </section>

      {/* Toggles */}
      <section className="bg-zinc-50 pt-10 pb-6" data-testid="configurator-toggles">
        <div className="container-oj space-y-6">
          <label data-testid="cfg-toggle-terminal" className="inline-flex items-center gap-3 cursor-pointer select-none">
            <span className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${withTerminal ? "" : "bg-zinc-300"}`} style={withTerminal ? { background: "#51dd7f" } : {}}>
              <input type="checkbox" className="sr-only" checked={withTerminal} onChange={() => setWithTerminal((v) => !v)} data-testid="cfg-toggle-terminal-input" />
              <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${withTerminal ? "translate-x-6" : "translate-x-1"}`} />
            </span>
            <span className="flex flex-col">
              <span className="text-sm font-semibold text-oj-ink">{t("configurator_page.with_term_label")}</span>
              <span className="text-[11px] text-zinc-500">{t("configurator_page.with_term_caption")}</span>
            </span>
          </label>

          <div data-testid="cfg-sales-tiers">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">{t("configurator_page.tier_intro")}</div>
            <div className="flex flex-wrap gap-2">
              {SALES_TIERS.map((s) => {
                const active = tierId === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`cfg-tier-${s.id}`}
                    onClick={() => setTierId(s.id)}
                    aria-pressed={active}
                    className={`text-sm font-semibold px-4 py-2.5 rounded-full border transition-all ${active ? "bg-oj-ink text-white border-transparent" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"}`}
                  >
                    {t(`configurator_page.tier_${s.id}`)} {s.discount > 0 && <span className={active ? "text-oj-brand" : "text-oj-kiosk"}>· {s.discount === 1 ? t("configurator_page.free_label") : `−${Math.round(s.discount * 100)}%`}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 sm:py-12 bg-zinc-50">
        <div className="container-oj grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-6">
            <HardwareTable
              testid="cfg-bundle-list"
              title={t("configurator_page.pos_bundle_table")}
              caption={t("configurator_page.pos_bundle_table_caption")}
              items={items.filter((i) => BUNDLE_SET.has(i.sku))}
              lineNetFor={lineNetFor}
              setQty={setQty}
              inc={inc}
              fmt={fmt}
              locale={locale}
              t={t}
              isFreeTier={isFreeTier}
              isBundleRow={true}
              footer={
                <div className="px-6 py-4 border-t border-zinc-200 flex flex-wrap items-center gap-3 bg-zinc-50/60">
                  <button
                    type="button"
                    onClick={addBundle}
                    data-testid="cfg-add-bundle"
                    className="inline-flex items-center gap-2 bg-oj-ink text-white text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-zinc-800 transition-colors"
                  >
                    <PackagePlus className="size-4" /> {t("configurator_page.bundle_label")}
                  </button>
                  <button
                    type="button"
                    onClick={resetBundle}
                    data-testid="cfg-reset-bundle"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-600 hover:text-oj-ink underline-offset-4 hover:underline"
                  >
                    <RotateCcw className="size-3.5" /> {t("configurator_page.bundle_reset")}
                  </button>
                  <span className="ml-auto text-[12px] text-zinc-500">{t("configurator_page.bundle_caption")}</span>
                </div>
              }
            />

            <HardwareTable
              testid="cfg-optional-list"
              title={t("configurator_page.optional_hw_table")}
              caption={t("configurator_page.optional_hw_table_caption")}
              items={items.filter((i) => !BUNDLE_SET.has(i.sku))}
              lineNetFor={lineNetFor}
              setQty={setQty}
              inc={inc}
              fmt={fmt}
              locale={locale}
              t={t}
              isFreeTier={isFreeTier}
              isBundleRow={false}
            />

            <div className="rounded-2xl bg-white border border-zinc-200 p-6 text-sm text-zinc-700 space-y-3" data-testid="configurator-notes">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.25em] text-zinc-500">
                <Info className="size-3.5" /> {t("configurator_page.important")}
              </div>
              <p>{t("configurator_page.note_tier_rental")}</p>
              <p>{t("configurator_page.note_rate")}</p>
              <p>{t("configurator_page.note_install")}</p>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div ref={summaryRef} className="lg:sticky lg:top-24 rounded-2xl bg-white border border-zinc-200 p-6 space-y-5" data-testid="configurator-summary">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">{t("configurator.total_hardware")}</div>
                {hardwareDiscount > 0 && (
                  <div className="mt-2 text-sm text-zinc-400 line-through">{fmt(hardwareGross, locale)}</div>
                )}
                <div data-testid="configurator-total-hardware" className="mt-1 font-display text-4xl font-black tracking-tighter text-oj-ink">
                  {fmt(hardwareNet, locale)}
                </div>
                {isFreeTier && (
                  <div className="mt-1 text-xs font-mono uppercase tracking-[0.15em] text-oj-kiosk">
                    {t("configurator_page.bundle_only")}
                  </div>
                )}
                {!isFreeTier && tier.discount > 0 && (
                  <div className="mt-1 text-xs font-mono uppercase tracking-[0.15em] text-oj-kiosk">
                    {`−${Math.round(tier.discount * 100)}% · ${t(`configurator_page.tier_${tier.id}`)}`}
                  </div>
                )}
              </div>

              <div>
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">{t("configurator.total_software")}</div>
                <div data-testid="configurator-total-software" className="mt-2 font-display text-2xl font-bold text-oj-ink">
                  {fmt(softwareMonthly, locale)} <span className="text-sm font-normal text-zinc-500">· {stations} {t("configurator_page.stations_label")}</span>
                </div>
                {withTerminal && stations > 0 && (
                  <div className="mt-1 text-xs font-mono uppercase tracking-[0.15em] text-oj-kiosk">{t("configurator_page.per_station_save")}</div>
                )}
              </div>

              <div className="space-y-2 pt-4 border-t border-zinc-200">
                <input data-testid="configurator-input-name" placeholder={t("configurator.placeholder_full_name")} value={contact.full_name} onChange={(e) => setContact({ ...contact, full_name: e.target.value })} className="w-full bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-oj-ink" />
                <input data-testid="configurator-input-business" placeholder={t("configurator.placeholder_business")} value={contact.business_name} onChange={(e) => setContact({ ...contact, business_name: e.target.value })} className="w-full bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-oj-ink" />
                <input data-testid="configurator-input-email" type="email" placeholder={t("configurator.placeholder_email")} value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="w-full bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-oj-ink" />
                <input data-testid="configurator-input-phone" type="tel" placeholder={locale === "fr" ? "Téléphone" : "Phone"} value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className="w-full bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-oj-ink" />
              </div>

              <div className="space-y-2">
                <button onClick={() => submit(false)} disabled={submitting} data-testid="configurator-save-cta" className="w-full inline-flex items-center justify-center gap-1.5 bg-oj-ink text-white font-semibold px-4 py-3 rounded-md hover:bg-zinc-800 disabled:opacity-60">
                  {t("configurator.cta_save")} <ArrowUpRight className="size-4" />
                </button>
                <button onClick={() => submit(true)} disabled={submitting} data-testid="configurator-callback-cta" className="w-full inline-flex items-center justify-center gap-1.5 bg-oj-brand text-oj-deep font-semibold px-4 py-3 rounded-md hover:bg-[#6FBDB9] disabled:opacity-60">
                  <Phone className="size-4" /> {t("configurator.cta_call")}
                </button>
              </div>

              <Link to="/#demo" className="block text-center text-sm text-zinc-600 hover:text-oj-ink">
                {t("configurator_page.book_demo_link")}
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* Mobile sticky quote bar — visible only on mobile, once the summary card has scrolled out of view */}
      <div
        data-testid="mobile-quote-bar"
        className={`lg:hidden fixed bottom-0 inset-x-0 z-40 transition-transform duration-300 ${
          showMobileBar ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <button
          type="button"
          onClick={() => summaryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
          className="w-full flex items-center justify-between gap-3 bg-oj-ink text-white px-4 py-3 shadow-[0_-8px_24px_rgba(0,0,0,0.15)]"
          aria-label={t("configurator_page.mobile_view_full")}
        >
          <span className="flex flex-col items-start">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{t("configurator_page.mobile_quote")}</span>
            <span className="font-display text-base font-bold leading-tight">
              {fmt(hardwareNet, locale)} <span className="text-zinc-400 font-normal text-xs">+ {fmt(softwareMonthly, locale)}/mo</span>
            </span>
          </span>
          <span className="inline-flex items-center gap-1.5 bg-oj-brand text-oj-deep font-semibold text-sm px-3 py-2 rounded-md">
            {t("configurator_page.mobile_view_full").split(" ").slice(-2).join(" ")} <ArrowUpRight className="size-4" />
          </span>
        </button>
      </div>
    </div>
  );
}


function HardwareTable({ testid, title, caption, items, lineNetFor, setQty, inc, fmt, locale, t, isFreeTier, isBundleRow, footer }) {
  return (
    <div className="rounded-2xl bg-white border border-zinc-200 overflow-hidden" data-testid={testid}>
      <div className="px-6 py-4 border-b border-zinc-200 flex items-baseline justify-between gap-4">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">{title}</div>
        {caption && <div className="text-[11px] text-zinc-500">{caption}</div>}
      </div>
      <ul className="divide-y divide-zinc-200">
        {items.map((it) => {
          const lineGross = it.qty * it.price;
          const lineNet = lineNetFor(it.sku, lineGross, it.qty);
          return (
            <li key={it.sku} className="flex items-center gap-4 px-6 py-4" data-testid={`hw-row-${it.sku}`}>
              <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: it.color }} />
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-oj-ink">{it.name}</div>
                <div className="text-xs font-mono text-zinc-500">{it.sku} · {fmt(it.price, locale)}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => inc(it.sku, -1)} data-testid={`hw-dec-${it.sku}`} className="size-9 grid place-items-center rounded-md border border-zinc-200 hover:border-zinc-900" aria-label="decrease">
                  <Minus className="size-4" />
                </button>
                <input
                  type="number"
                  min="0"
                  max="50"
                  value={it.qty}
                  onChange={(e) => setQty((s) => ({ ...s, [it.sku]: Math.max(0, Math.min(50, parseInt(e.target.value || "0", 10))) }))}
                  data-testid={`hw-qty-${it.sku}`}
                  className="w-12 text-center bg-zinc-50 border border-zinc-200 rounded-md py-1.5 text-sm font-semibold focus:outline-none focus:border-oj-ink"
                />
                <button onClick={() => inc(it.sku, +1)} data-testid={`hw-inc-${it.sku}`} className="size-9 grid place-items-center rounded-md border border-zinc-200 hover:border-zinc-900" aria-label="increase">
                  <Plus className="size-4" />
                </button>
                <div className="ml-3 hidden sm:flex flex-col items-end w-28">
                  {lineGross > 0 && lineNet < lineGross && (
                    <span className="text-[11px] text-zinc-400 line-through">{fmt(lineGross, locale)}</span>
                  )}
                  <span className="font-display font-bold text-oj-ink">{fmt(lineNet, locale)}</span>
                  {isFreeTier && isBundleRow && it.qty > 0 && (
                    <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-oj-kiosk">{t("configurator_page.free_label")}</span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {footer}
    </div>
  );
}
